import React from 'react'
import classnames from 'classnames'
import { Skeleton, Table } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { NOTIFICATION_THUMBNAIL_SIZE } from './notification-thumbnail-size'
import { DASHBOARD_TABLE_ROW_CLASS_NAME, DASHBOARD_TABLE_SIZE, dashboardTableClassName } from './dashboard-table-parts'
import { tableRowHeightStyle } from './table-row-heights'
import { TABLE_TOTALS_ROW_CLASS_NAME } from './table-totals'
import './org-dashboard.scss'

/** What a placeholder row holds in a column. A one-line figure by default. */
export type TableSkeletonShape = 'figure' | 'media' | 'two-lines' | 'rank'

/** The width of the rank placeholder; the rank column is sized to hold exactly this inside its cell padding. */
const RANK_PLACEHOLDER_WIDTH = 16

type PlaceholderRow = { key: number }

const PlaceholderCell: React.FC<{ shape: TableSkeletonShape | undefined; align: 'left' | 'right' | undefined }> = ({
    shape,
    align,
}) => {
    switch (shape) {
        case 'media':
            return (
                <div className="org-dashboard-table-skeleton-media">
                    <Skeleton.Node
                        active
                        className="org-dashboard-table-skeleton-thumbnail"
                        style={{ width: NOTIFICATION_THUMBNAIL_SIZE, height: NOTIFICATION_THUMBNAIL_SIZE }}
                    >
                        <span />
                    </Skeleton.Node>
                    <div className="org-dashboard-table-skeleton-lines">
                        <Skeleton.Input active size="small" block />
                        <Skeleton.Input active size="small" block style={{ width: '70%' }} />
                    </div>
                </div>
            )
        case 'two-lines':
            return (
                <div className="org-dashboard-table-skeleton-lines">
                    <Skeleton.Input active size="small" block style={{ width: '60%' }} />
                    <Skeleton.Input active size="small" block style={{ width: '45%' }} />
                </div>
            )
        case 'rank':
            return (
                <Skeleton.Input
                    active
                    size="small"
                    style={{ width: RANK_PLACEHOLDER_WIDTH, minWidth: RANK_PLACEHOLDER_WIDTH }}
                />
            )
        default:
            return <Skeleton.Input active size="small" style={{ width: align === 'right' ? 56 : '60%' }} />
    }
}

type Props<Row> = {
    /**
     * The loaded table's own columns. The placeholder is the same table with its rows replaced, so headings,
     * widths, alignment, sorter markup and layout are the loaded table's by construction and cannot drift apart.
     */
    columns: ColumnsType<Row>
    /** What a row holds in each column, by column key. */
    shapes?: Record<string, TableSkeletonShape>
    rowCount: number
    /** Matches the height of a loaded row, so nothing below the table moves when the rows arrive. */
    rowHeight: number
    /** The loaded table's own class: it carries the cell padding, header treatment and row height. */
    className: string
    scroll?: TableProps<Row>['scroll']
    tableLayout?: TableProps<Row>['tableLayout']
    /**
     * Set for a table that closes with a Total row, by the keys of the columns that hold a label or a figure in it;
     * any other column is blank, as in the loaded row. The placeholder then ends in the same Total row shell, so
     * nothing below the table moves when the totals arrive.
     */
    totalsShapes?: Record<string, 'label' | 'figure'>
}

/**
 * A table while its rows load: the loaded table's own headings and column widths over placeholder rows of a loaded
 * row's height, inside the card's own padding. Placeholder bars stand where the figures will be.
 */
export const TableSkeleton = <Row,>({
    columns,
    shapes = {},
    rowCount,
    rowHeight,
    className,
    scroll,
    tableLayout,
    totalsShapes,
}: Props<Row>): React.ReactElement => {
    const placeholderColumns = (columns as ColumnsType<PlaceholderRow>).map((column) => ({
        ...column,
        render: () => (
            <div
                className={classnames('org-dashboard-table-skeleton-cell', {
                    'is-right': 'align' in column && column.align === 'right',
                })}
            >
                <PlaceholderCell
                    shape={shapes[String(column.key)]}
                    align={'align' in column && column.align === 'right' ? 'right' : 'left'}
                />
            </div>
        ),
    })) as ColumnsType<PlaceholderRow>
    return (
        <div className="org-dashboard-table-skeleton" aria-busy="true">
            <Table<PlaceholderRow>
                className={dashboardTableClassName(className)}
                size={DASHBOARD_TABLE_SIZE}
                columns={placeholderColumns}
                dataSource={Array.from({ length: rowCount }, (_, key) => ({ key }))}
                pagination={false}
                scroll={scroll}
                tableLayout={tableLayout}
                style={tableRowHeightStyle(rowHeight)}
                summary={
                    totalsShapes
                        ? () => (
                              <Table.Summary fixed="bottom">
                                  <Table.Summary.Row className={TABLE_TOTALS_ROW_CLASS_NAME}>
                                      {columns.map((column, index) => {
                                          const shape = totalsShapes[String(column.key)]
                                          const align = 'align' in column && column.align === 'right' ? 'right' : 'left'
                                          return (
                                              <Table.Summary.Cell index={index} key={String(column.key)} align={align}>
                                                  {shape === 'label' ? (
                                                      <span className="org-dashboard-totals-label">
                                                          <Skeleton.Input active size="small" style={{ width: 96 }} />
                                                          <span
                                                              className="org-dashboard-totals-label-icon-placeholder"
                                                              aria-hidden="true"
                                                          />
                                                      </span>
                                                  ) : shape ? (
                                                      <Skeleton.Input active size="small" style={{ width: 56 }} />
                                                  ) : null}
                                              </Table.Summary.Cell>
                                          )
                                      })}
                                  </Table.Summary.Row>
                              </Table.Summary>
                          )
                        : undefined
                }
                onRow={() => ({
                    className: classnames(DASHBOARD_TABLE_ROW_CLASS_NAME, 'org-dashboard-table-skeleton-row'),
                    style: { height: rowHeight },
                })}
            />
        </div>
    )
}
